// Planning rules for the schedule generator.
//
// This is the single place scheduling/planning rules live, so the calendar
// generator can be steered without hand-editing individual sessions.
//
// Two kinds of rules are supported:
//   1. Exclusions  â€” course-name keywords whose sessions are dropped from
//                     analysis AND draft generation (hard rule).
//   2. Planning notes â€” free-text guidance fed to the AI planning assistant
//                     to help managers reason about the schedule (advisory).
//
// Built-in exclusions always apply. Managers can layer additional exclusions
// and planning notes on top via the "Planning Rules" tab; those extras are
// persisted per-browser in localStorage.

// Built-in exclusions that always apply and cannot be removed in the UI.
// Any session whose name contains one of these (case-insensitive substring)
// is excluded from the generator.
export const DEFAULT_EXCLUDE_KEYWORDS = ['IYT', 'Special', 'NOAA'];

// Exported so the server-sync layer (planningRulesSync.js) can keep the
// localStorage cache consistent with the shared store.
export const STORAGE_KEY = 'mi_schedule_planning_rules';

// True if a course name should be excluded from the generator.
export const isExcludedName = (name, extraKeywords = []) => {
  if (!name || typeof name !== 'string') return false;
  const lower = name.toLowerCase();
  const keywords = [...DEFAULT_EXCLUDE_KEYWORDS, ...(extraKeywords || [])];
  return keywords.some(k => k && lower.includes(String(k).toLowerCase()));
};

// ─────────────────────────────────────────────────────────────────────────
// Structured scheduling rules ("Tier 1" enforcement).
//
// A fixed taxonomy of rule TYPES, each with parameters and a HARD/SOFT
// severity, is the guardrail: managers can only express reusable policies,
// never one-off "move this session to that date" instructions. Enforcement
// is deterministic (no AI in the loop) and runs in the generator.
// ─────────────────────────────────────────────────────────────────────────

export const RULE_TYPES = {
  MIN_GAP: 'min-gap',                       // same course: ≥ N days between sessions
  NO_OVERLAP_SAME: 'no-overlap-same',       // same course: sessions never overlap
  START_DAY_OF_WEEK: 'start-day-of-week',   // course starts only on selected weekdays
  NO_OVERLAP_GROUP: 'no-overlap-group',     // a group of courses: none overlap each other
  SAME_WEEK_BAN: 'same-week-ban',           // two courses: never in the same week
  SEQUENCE: 'sequence',                     // course B must start after course A
  ORDERED_GROUP: 'ordered-group',           // courses scheduled together, in this exact order
  AVOID_CROSS_LOCATION: 'avoid-cross-location', // same course: avoid simultaneous across locations
  LOCATION_BLACKOUT: 'location-blackout',   // no sessions at a location, optionally for one year
  LOCATION_REASSIGN: 'location-reassign'    // move a location stream, preserving session count
};

// Operational location changes that must apply to every newly generated draft.
// Edmonds has been absorbed by Everett, so this is enforced independently of
// user-entered notes and shared planning-rule storage.
export const BUILT_IN_SCHEDULING_RULES = [{
  id: 'built-in-edmonds-to-everett',
  type: RULE_TYPES.LOCATION_REASSIGN,
  severity: 'hard',
  enabled: true,
  builtIn: true,
  fromLocation: 'Edmonds, WA',
  toLocation: 'Everett, WA',
  targetYear: null
}];

// UI metadata: label, help text, default severity, and which fields apply.
export const RULE_TYPE_META = {
  [RULE_TYPES.MIN_GAP]: {
    label: 'Minimum gap between same course',
    help: 'e.g. Basic Training sessions ≥ 14 days apart in San Diego',
    fields: ['courseA', 'location', 'gapDays'],
    defaultSeverity: 'hard'
  },
  [RULE_TYPES.NO_OVERLAP_SAME]: {
    label: 'No overlap — same course',
    help: 'Two sessions of the same course never run at the same time',
    fields: ['courseA', 'location'],
    defaultSeverity: 'hard'
  },
  [RULE_TYPES.START_DAY_OF_WEEK]: {
    label: 'Start only on selected weekdays',
    help: 'Hard scheduling constraint: the selected course starts only on the checked weekdays at the optional location',
    fields: ['courseA', 'location', 'daysOfWeek'],
    defaultSeverity: 'hard'
  },
  [RULE_TYPES.NO_OVERLAP_GROUP]: {
    label: 'No overlap — group of courses',
    help: 'e.g. QMED-FOWT and Fireman/Oiler Water Tender QMED never overlap',
    fields: ['courses', 'location'],
    defaultSeverity: 'hard'
  },
  [RULE_TYPES.SAME_WEEK_BAN]: {
    label: 'Not the same week (two courses)',
    help: 'e.g. ABLE Seaman never the same week as Proficiency in Survival Craft',
    fields: ['courseA', 'courseB', 'location'],
    defaultSeverity: 'hard'
  },
  [RULE_TYPES.SEQUENCE]: {
    label: 'Order — course B after course A',
    help: 'e.g. Proficiency in Survival Craft after ABLE Seaman',
    fields: ['courseA', 'courseB', 'location'],
    defaultSeverity: 'hard'
  },
  [RULE_TYPES.ORDERED_GROUP]: {
    label: 'Ordered group - schedule together, in order',
    help: 'List 2+ courses in their exact order and set the maximum instructional days from the first course starting through the final course ending.',
    fields: ['courses', 'location', 'gapDays'],
    defaultSeverity: 'hard'
  },
  [RULE_TYPES.AVOID_CROSS_LOCATION]: {
    label: 'Avoid same course at the same time across locations',
    help: 'Spread popular courses so they are not simultaneous at different sites',
    fields: ['courseA'],
    defaultSeverity: 'soft'
  },
  [RULE_TYPES.LOCATION_BLACKOUT]: {
    label: 'Do not schedule at location',
    help: 'Hard exclusion: no classes will be generated at this location for the selected year (leave year blank for all years)',
    fields: ['location', 'targetYear'],
    defaultSeverity: 'hard'
  },
  [RULE_TYPES.LOCATION_REASSIGN]: {
    label: 'Reassign one location to another',
    help: 'Moves every generated class from the original location to the replacement location while preserving the session count',
    fields: ['fromLocation', 'toLocation', 'targetYear'],
    defaultSeverity: 'hard'
  }
};

export const makeRuleId = () => `rule-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

const normalizeRuleText = (value) => String(value || '').trim().toLowerCase();

// Course and location values come from exact dropdown options. Exact matching
// prevents a rule for "Basic Training" from also changing "Basic Training
// Revalidation" (or one campus name from matching another by substring).
export const courseMatches = (name, term) => {
  if (!name || !term) return false;
  return normalizeRuleText(name) === normalizeRuleText(term);
};

// A rule's location filter: empty term means "all locations".
export const locationMatches = (locationName, term) => {
  if (!term) return true;
  if (!locationName) return false;
  return normalizeRuleText(locationName) === normalizeRuleText(term);
};

// Course exclusions take precedence over structured rules. Keep rules in
// storage so they become active again if a course is re-included, but do not
// pass a rule to generation while any course it references is absent.
export const filterSchedulingRulesForCourses = (rules = [], courseNames = []) => {
  const included = new Set((courseNames || []).map(normalizeRuleText).filter(Boolean));
  return (rules || []).filter(rule => {
    const fields = RULE_TYPE_META[rule?.type]?.fields || [];
    const referencedCourses = [
      ...(fields.includes('courseA') ? [rule.courseA] : []),
      ...(fields.includes('courseB') ? [rule.courseB] : []),
      ...(fields.includes('courses') && Array.isArray(rule.courses) ? rule.courses : [])
    ].map(normalizeRuleText).filter(Boolean);
    return referencedCourses.every(course => included.has(course));
  });
};

// Validate a complete structured rule set before it is saved or used to
// generate a draft. Returns user-readable errors; an empty array is valid.
export const validateSchedulingRules = (rules = [], options = {}) => {
  const active = (rules || []).filter(rule => rule && rule.enabled !== false);
  const knownCourses = new Set((options.courseNames || []).map(normalizeRuleText).filter(Boolean));
  const errors = [];
  const add = (message) => { if (!errors.includes(message)) errors.push(message); };
  const requireCourse = (name, label) => {
    if (!name) add(`${label} is required.`);
    else if (knownCourses.size > 0 && !knownCourses.has(normalizeRuleText(name))) {
      add(`Course "${name}" is not included in this draft.`);
    }
  };

  active.forEach(rule => {
    if (!RULE_TYPE_META[rule.type]) {
      add(`Unknown scheduling rule type: ${rule.type || '(blank)'}.`);
      return;
    }
    const fields = RULE_TYPE_META[rule.type].fields;
    if (fields.includes('courseA')) requireCourse(rule.courseA, 'Course');
    if (fields.includes('courseB')) requireCourse(rule.courseB, 'Second course');
    if (fields.includes('courses')) {
      if (!Array.isArray(rule.courses) || rule.courses.length < 2) {
        add(`${RULE_TYPE_META[rule.type].label} requires at least two courses.`);
      } else {
        rule.courses.forEach(course => requireCourse(course, 'Course'));
        if (new Set(rule.courses.map(normalizeRuleText)).size !== rule.courses.length) {
          add(`${RULE_TYPE_META[rule.type].label} cannot contain the same course more than once.`);
        }
      }
    }
    if (fields.includes('daysOfWeek')) {
      const days = rule.daysOfWeek || [];
      if (!Array.isArray(days) || days.length === 0 || days.some(day => !Number.isInteger(day) || day < 0 || day > 6)) {
        add('Start-day rules require at least one valid weekday.');
      }
    }
    if (fields.includes('gapDays') && (!Number.isInteger(Number(rule.gapDays)) || Number(rule.gapDays) < 1)) {
      add(`${RULE_TYPE_META[rule.type].label} requires a positive whole-day gap.`);
    }
    if (fields.includes('targetYear') && rule.targetYear != null && rule.targetYear !== '' &&
        (!Number.isInteger(Number(rule.targetYear)) || Number(rule.targetYear) < 2020 || Number(rule.targetYear) > 2100)) {
      add('Rule year must be a whole number from 2020 through 2100.');
    }
    if (fields.includes('location') && rule.type === RULE_TYPES.LOCATION_BLACKOUT && !rule.location) {
      add('Location blackout requires a location.');
    }
    if (rule.type === RULE_TYPES.LOCATION_REASSIGN) {
      if (!rule.fromLocation || !rule.toLocation) add('Location reassignment requires both locations.');
      if (normalizeRuleText(rule.fromLocation) === normalizeRuleText(rule.toLocation)) {
        add('Location reassignment must use two different locations.');
      }
    }
    if ([RULE_TYPES.SEQUENCE, RULE_TYPES.SAME_WEEK_BAN].includes(rule.type) &&
        normalizeRuleText(rule.courseA) === normalizeRuleText(rule.courseB)) {
      add(`${RULE_TYPE_META[rule.type].label} must use two different courses.`);
    }
  });

  // Multiple hard weekday rules are combined by intersection. Reject an empty
  // intersection rather than generating a draft that must violate one rule.
  const hardWeekdayRules = active.filter(rule =>
    rule.type === RULE_TYPES.START_DAY_OF_WEEK && rule.severity !== 'soft'
  );
  const weekdayCourses = new Set(hardWeekdayRules.map(rule => normalizeRuleText(rule.courseA)));
  weekdayCourses.forEach(course => {
    const courseRules = hardWeekdayRules.filter(rule => normalizeRuleText(rule.courseA) === course);
    const locations = new Set(courseRules.map(rule => normalizeRuleText(rule.location)).filter(Boolean));
    const scopes = locations.size > 0 ? [...locations] : [''];
    scopes.forEach(location => {
      const applicable = courseRules.filter(rule =>
        !rule.location || normalizeRuleText(rule.location) === location
      );
      if (applicable.length === 0) return;
      const intersection = applicable.slice(1).reduce(
        (days, rule) => days.filter(day => (rule.daysOfWeek || []).includes(day)),
        [...(applicable[0].daysOfWeek || [])]
      );
      if (intersection.length === 0) {
        add(`Hard start-day rules for "${applicable[0].courseA}"${location ? ` at ${applicable.find(rule => normalizeRuleText(rule.location) === location)?.location || location}` : ''} have no allowed weekday in common.`);
      }
    });
  });

  // Sequence and ordered-group rules form directed course dependencies. A hard
  // cycle cannot be scheduled, so reject it before placement begins.
  const dependencyScopes = new Map();
  const addDependency = (location, from, to) => {
    const scope = normalizeRuleText(location);
    if (!dependencyScopes.has(scope)) dependencyScopes.set(scope, new Map());
    const graph = dependencyScopes.get(scope);
    const key = normalizeRuleText(from);
    if (!graph.has(key)) graph.set(key, new Set());
    graph.get(key).add(normalizeRuleText(to));
  };
  active.filter(rule => rule.severity !== 'soft').forEach(rule => {
    if (rule.type === RULE_TYPES.SEQUENCE && rule.courseA && rule.courseB) {
      addDependency(rule.location, rule.courseA, rule.courseB);
    }
    if (rule.type === RULE_TYPES.ORDERED_GROUP && Array.isArray(rule.courses)) {
      for (let i = 1; i < rule.courses.length; i++) {
        addDependency(rule.location, rule.courses[i - 1], rule.courses[i]);
      }
    }
  });
  const graphHasCycle = (graph) => {
    const visiting = new Set();
    const visited = new Set();
    const hasCycle = (node) => {
      if (visiting.has(node)) return true;
      if (visited.has(node)) return false;
      visiting.add(node);
      for (const next of graph.get(node) || []) if (hasCycle(next)) return true;
      visiting.delete(node);
      visited.add(node);
      return false;
    };
    return [...graph.keys()].some(hasCycle);
  };
  const mergeGraphs = (...graphs) => {
    const merged = new Map();
    graphs.filter(Boolean).forEach(graph => graph.forEach((destinations, from) => {
      if (!merged.has(from)) merged.set(from, new Set());
      destinations.forEach(to => merged.get(from).add(to));
    }));
    return merged;
  };
  const globalDependencies = dependencyScopes.get('');
  if (globalDependencies && graphHasCycle(globalDependencies)) {
    add('Hard sequence or ordered-group rules contain a course cycle.');
  }
  dependencyScopes.forEach((graph, scope) => {
    if (scope && graphHasCycle(mergeGraphs(globalDependencies, graph))) {
      add('Hard sequence or ordered-group rules contain a course cycle.');
    }
  });

  // Reject conflicting reassignment destinations and cycles. Chained moves are
  // valid (A→B→C) and are resolved by the generator.
  const reassignments = active.filter(rule => rule.type === RULE_TYPES.LOCATION_REASSIGN);
  const byScope = new Map();
  reassignments.forEach(rule => {
    const scope = String(rule.targetYear || 'all');
    if (!byScope.has(scope)) byScope.set(scope, new Map());
    const routes = byScope.get(scope);
    const from = normalizeRuleText(rule.fromLocation);
    const to = normalizeRuleText(rule.toLocation);
    if (routes.has(from) && routes.get(from) !== to) {
      add(`Location ${rule.fromLocation} has conflicting reassignment destinations for ${scope === 'all' ? 'all years' : scope}.`);
    }
    routes.set(from, to);
  });
  const routesHaveCycle = (routes) => {
    let cycle = false;
    routes.forEach((_, start) => {
      const seen = new Set();
      let current = start;
      while (routes.has(current)) {
        if (seen.has(current)) {
          cycle = true;
          break;
        }
        seen.add(current);
        current = routes.get(current);
      }
    });
    return cycle;
  };
  const allYearRoutes = byScope.get('all') || new Map();
  if (routesHaveCycle(allYearRoutes)) add('Location reassignment rules contain a cycle.');
  byScope.forEach((routes, scope) => {
    if (scope === 'all') return;
    const combined = new Map(allYearRoutes);
    routes.forEach((to, from) => combined.set(from, to));
    if (routesHaveCycle(combined)) add('Location reassignment rules contain a cycle.');
  });

  return errors;
};

// Human-readable one-line description of a rule (for the UI and the AI prompt).
export const describeRule = (rule) => {
  if (!rule) return '';
  const sev = rule.severity === 'soft' ? 'SOFT' : 'HARD';
  const at = rule.location ? ` at ${rule.location}` : '';
  switch (rule.type) {
    case RULE_TYPES.MIN_GAP:
      return `[${sev}] "${rule.courseA}" sessions ≥ ${rule.gapDays || 14} days apart${at}`;
    case RULE_TYPES.NO_OVERLAP_SAME:
      return `[${sev}] "${rule.courseA}" sessions never overlap${at}`;
    case RULE_TYPES.START_DAY_OF_WEEK: {
      const weekdayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      const days = (rule.daysOfWeek || []).map(day => weekdayNames[day]).filter(Boolean).join(', ');
      return `[${sev}] "${rule.courseA}" starts only on ${days || 'selected weekdays'}${at}`;
    }
    case RULE_TYPES.NO_OVERLAP_GROUP:
      return `[${sev}] These never overlap${at}: ${(rule.courses || []).join(', ')}`;
    case RULE_TYPES.SAME_WEEK_BAN:
      return `[${sev}] "${rule.courseA}" never the same week as "${rule.courseB}"${at}`;
    case RULE_TYPES.SEQUENCE:
      return `[${sev}] "${rule.courseB}" scheduled after "${rule.courseA}"${at}`;
    case RULE_TYPES.ORDERED_GROUP:
      return `[${sev}] Complete group in order within ${rule.gapDays || 7} instructional days${at}: ${(rule.courses || []).join(' \u2192 ')}`;
    case RULE_TYPES.AVOID_CROSS_LOCATION:
      return `[${sev}] Avoid "${rule.courseA}" running simultaneously across locations`;
    case RULE_TYPES.LOCATION_BLACKOUT:
      return `[HARD] Do not schedule any classes at ${rule.location}${rule.targetYear ? ` in ${rule.targetYear}` : ''}`;
    case RULE_TYPES.LOCATION_REASSIGN:
      return `[HARD] Reassign all classes from ${rule.fromLocation} to ${rule.toLocation}${rule.targetYear ? ` in ${rule.targetYear}` : ''}`;
    default:
      return `[${sev}] ${rule.type}`;
  }
};

// Multi-line summary of all enabled rules for the AI planning assistant.
export const summarizeRulesForAI = (rules = []) =>
  (rules || []).filter(r => r.enabled !== false).map((r, i) => `${i + 1}. ${describeRule(r)}`).join('\n');

// Default (empty) user-editable rule set.
const emptyRules = () => ({
  excludeKeywords: [],     // extra name keywords to exclude (on top of defaults)
  excludedCourseKeys: [],  // specific course profile keys (mi_course GUIDs) the
                           // manager has toggled OFF so they are skipped when
                           // generating a draft (e.g. one-off special groups)
  courseProfileOptions: [], // latest exact included profile list [{ key, name }]
  planningNotes: [],       // [{ id, text }] free-text guidance for the AI assistant
  schedulingRules: []      // [rule] structured, enforced rules (the taxonomy above)
});

export const normalizePlanningRules = (rules = {}) => ({
  excludeKeywords: Array.isArray(rules.excludeKeywords) ? rules.excludeKeywords : [],
  excludedCourseKeys: Array.isArray(rules.excludedCourseKeys) ? rules.excludedCourseKeys : [],
  courseProfileOptions: Array.isArray(rules.courseProfileOptions) ? rules.courseProfileOptions : [],
  planningNotes: Array.isArray(rules.planningNotes) ? rules.planningNotes : [],
  schedulingRules: Array.isArray(rules.schedulingRules) ? rules.schedulingRules : []
});

// Load the manager-defined rules from localStorage. Always returns a valid
// shape, even if storage is empty or corrupt.
export const loadPlanningRules = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return emptyRules();
    return normalizePlanningRules(JSON.parse(raw));
  } catch (e) {
    console.warn('Failed to load planning rules, using defaults:', e);
    return emptyRules();
  }
};

// Persist the manager-defined rules.
export const savePlanningRules = (rules) => {
  try {
    const safe = normalizePlanningRules(rules);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(safe));
    return safe;
  } catch (e) {
    console.warn('Failed to save planning rules:', e);
    return rules;
  }
};
